/* ThemeStudio — modal where the user designs and saves calendar themes.
 * a/b/c = gradient, pattern, accent
 * e     = title + subtitle + font
 * f     = sticky-note colors + custom labels
 * i     = upload custom background image
 * j     = AI-generate from description
 *
 * Pure design tool — Bujo features still work; the saved theme overrides colors. */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, X, Save, Trash2, Sparkles, Upload, Plus, Check, Palette as PaletteIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATTERN_LABELS = [
  { id: 'facets',  label: '💎 Ametist' },
  { id: 'dots',    label: '⚫ Polka dots' },
  { id: 'flowers', label: '🌸 Blommor' },
  { id: 'paper',   label: '📄 Papper' },
  { id: 'plain',   label: '✨ Slätt' },
  { id: 'custom',  label: '🖼️ Egen bild' },
];

const FONT_OPTIONS = [
  { id: 'serif',   label: 'Serif (elegant)' },
  { id: 'sans',    label: 'Sans-serif (rent)' },
  { id: 'script',  label: 'Script (handskrivet)' },
  { id: 'display', label: 'Display (statement)' },
];

const NOTE_COLORS = ['pink', 'rose', 'yellow', 'violet', 'sky', 'mint'];

const PRESETS = [
  { name: '💜 Lavendel-planner', title: 'MONTHLY PLAN', subtitle: '2026', title_font: 'sans',
    gradient_from: '#FFFFFF', gradient_via: '#FFFFFF', gradient_to: '#FFFFFF', gradient_dir: '135deg',
    pattern: 'plain', accent: '#A78BFA', accent_soft: '#DDD6FE',
    cell_bg: 'rgba(255,255,255,1)', border_color: '#C4B5FD', text_color: '#5B21B6',
    corner_decor: 'none' },
  { name: '💎 Ametist', title: 'AMETHYST PLAN', subtitle: '— 2026 —', title_font: 'serif',
    gradient_from: '#F3EBFF', gradient_via: '#E9D5FF', gradient_to: '#FCE7F3', gradient_dir: '135deg',
    pattern: 'facets', accent: '#9333EA', accent_soft: '#C084FC',
    cell_bg: 'rgba(255,255,255,0.8)', border_color: '#D8B4FE', text_color: '#581C87',
    corner_decor: 'gem' },
  { name: '🌸 Söndagsmorgon', title: 'SUNDAY VIBES', subtitle: 'slow & soft', title_font: 'script',
    gradient_from: '#FFF7F0', gradient_via: '#FFE8E8', gradient_to: '#FFE0F0', gradient_dir: '135deg',
    pattern: 'flowers', accent: '#EC4899', accent_soft: '#F9A8D4',
    cell_bg: 'rgba(255,255,255,0.85)', border_color: '#FBCFE8', text_color: '#831843',
    corner_decor: 'flower' },
  { name: '🌙 Vintervila', title: 'COZY DAYS', subtitle: 'hibernate', title_font: 'serif',
    gradient_from: '#E0E7FF', gradient_via: '#DDD6FE', gradient_to: '#F3F4F6', gradient_dir: '160deg',
    pattern: 'paper', accent: '#6366F1', accent_soft: '#A5B4FC',
    cell_bg: 'rgba(255,255,255,0.75)', border_color: '#C7D2FE', text_color: '#312E81',
    corner_decor: 'none' },
  { name: '🍑 Sommarfika', title: 'SUMMER MOOD', subtitle: 'fika tid', title_font: 'display',
    gradient_from: '#FFF7ED', gradient_via: '#FED7AA', gradient_to: '#FECACA', gradient_dir: '120deg',
    pattern: 'dots', accent: '#EA580C', accent_soft: '#FB923C',
    cell_bg: 'rgba(255,255,255,0.8)', border_color: '#FDBA74', text_color: '#7C2D12',
    corner_decor: 'heart' },
];

function ColorRow({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs font-bold">
      <span className="flex-1 text-gray-700">{label}</span>
      <input type="color" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-12 h-7 rounded cursor-pointer border border-gray-200" />
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} maxLength={20} className="w-20 px-1.5 py-0.5 rounded border border-gray-200 text-[10px] font-mono" />
    </label>
  );
}

function ThemeEditor({ initial, onSave, onCancel, busy }) {
  const [t, setT] = useState({ ...initial });
  const set = (k, v) => setT((s) => ({ ...s, [k]: v }));

  const pattern = t.pattern || 'facets';
  const previewBg = `linear-gradient(${t.gradient_dir || '135deg'}, ${t.gradient_from || '#fff'} 0%, ${t.gradient_via || '#fff'} 50%, ${t.gradient_to || '#fff'} 100%)`;

  const uploadBg = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/cal-theme/upload-bg`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('pattern', 'custom');
      set('custom_bg_asset_id', r.data.asset_id);
      set('custom_bg_b64', r.data.data_b64);  // keep locally for instant preview
      set('custom_bg_mime', r.data.mime);
      toast.success('Bakgrund uppladdad 📸');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte ladda upp');
    }
  };

  const updateNote = (idx, key, val) => {
    const next = [...(t.sticky_notes || [])];
    next[idx] = { ...next[idx], [key]: val };
    set('sticky_notes', next);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
      <div className="space-y-3 overflow-y-auto max-h-[68vh] pr-1">
        <div>
          <label className="text-xs font-bold text-gray-700">Tema-namn (bara för dig)</label>
          <input value={t.name || ''} onChange={(e) => set('name', e.target.value)} maxLength={40} data-testid="theme-name" className="w-full px-2 py-1 rounded border border-gray-200 text-sm" />
        </div>

        <div className="bg-gray-50 rounded-xl p-2.5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">A · Gradient</div>
          <ColorRow label="Från" value={t.gradient_from} onChange={(v) => set('gradient_from', v)} testid="grad-from" />
          <ColorRow label="Via"  value={t.gradient_via}  onChange={(v) => set('gradient_via', v)} testid="grad-via" />
          <ColorRow label="Till" value={t.gradient_to}   onChange={(v) => set('gradient_to', v)} testid="grad-to" />
          <select value={t.gradient_dir || '135deg'} onChange={(e) => set('gradient_dir', e.target.value)} data-testid="grad-dir" className="w-full px-2 py-1 rounded border border-gray-200 text-xs">
            <option value="135deg">↘ Diagonal</option>
            <option value="90deg">→ Höger</option>
            <option value="180deg">↓ Ner</option>
            <option value="45deg">↗ Upp-höger</option>
            <option value="to right">→ Horisontal</option>
          </select>
        </div>

        <div className="bg-gray-50 rounded-xl p-2.5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">B · Mönster</div>
          <div className="grid grid-cols-3 gap-1">
            {PATTERN_LABELS.map((p) => (
              <button key={p.id} onClick={() => set('pattern', p.id)} data-testid={`pat-${p.id}`} className={`text-[10px] font-bold py-1 rounded border-2 ${pattern === p.id ? 'bg-violet-500 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-200'}`}>{p.label}</button>
            ))}
          </div>
          {pattern === 'custom' && (
            <>
              <label className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl border-2 border-dashed border-violet-300 cursor-pointer hover:bg-violet-50 text-xs text-violet-700">
                <Upload className="w-3.5 h-3.5" /> {t.custom_bg_asset_id ? 'Byt egen bild' : 'Ladda upp egen bild'}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadBg} data-testid="bg-upload" className="hidden" />
              </label>
              {(t.custom_bg_asset_id || t.custom_bg_b64) && (
                <label className="flex items-start gap-2 px-2 py-2 rounded-xl bg-violet-50 border border-violet-200 cursor-pointer hover:bg-violet-100 text-[11px] text-violet-900">
                  <input
                    type="checkbox"
                    checked={!!t.template_mode}
                    onChange={(e) => set('template_mode', e.target.checked)}
                    data-testid="template-mode-toggle"
                    className="mt-0.5 accent-violet-600"
                  />
                  <span className="flex-1">
                    <span className="font-extrabold">🪧 Använd bilden som planner-mall</span>
                    <span className="block text-[10px] text-violet-700/80 italic mt-0.5">
                      Bilden blir hela kalendern, cellerna blir transparenta så ditt mönster syns igenom. Tips: ladda upp en bild med 7 kolumner och 5–6 rader.
                    </span>
                  </span>
                </label>
              )}
            </>
          )}
        </div>

        <div className="bg-gray-50 rounded-xl p-2.5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">C · Accenter</div>
          <ColorRow label="Accent (idag, highlights)" value={t.accent}       onChange={(v) => set('accent', v)} testid="accent" />
          <ColorRow label="Accent soft (lavendel)"    value={t.accent_soft}  onChange={(v) => set('accent_soft', v)} testid="accent-soft" />
          <ColorRow label="Border / linjer"           value={t.border_color} onChange={(v) => set('border_color', v)} testid="border" />
          <ColorRow label="Text-färg"                 value={t.text_color}   onChange={(v) => set('text_color', v)} testid="text-color" />
        </div>

        <div className="bg-gray-50 rounded-xl p-2.5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">E · Titel</div>
          <input value={t.title || ''} onChange={(e) => set('title', e.target.value.slice(0, 30))} placeholder="Titel (ALL CAPS)" data-testid="title" className="w-full px-2 py-1 rounded border border-gray-200 text-sm" />
          <input value={t.subtitle || ''} onChange={(e) => set('subtitle', e.target.value.slice(0, 30))} placeholder="Undertitel" data-testid="subtitle" className="w-full px-2 py-1 rounded border border-gray-200 text-xs" />
          <select value={t.title_font || 'serif'} onChange={(e) => set('title_font', e.target.value)} data-testid="title-font" className="w-full px-2 py-1 rounded border border-gray-200 text-xs">
            {FONT_OPTIONS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <select value={t.corner_decor || 'gem'} onChange={(e) => set('corner_decor', e.target.value)} data-testid="corner" className="w-full px-2 py-1 rounded border border-gray-200 text-xs">
            <option value="gem">💎 Gem i hörnet</option>
            <option value="flower">🌸 Blomma</option>
            <option value="butterfly">🦋 Fjäril</option>
            <option value="heart">💖 Hjärta</option>
            <option value="none">Inget</option>
          </select>
        </div>

        <div className="bg-gray-50 rounded-xl p-2.5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">F · Sticky-notes</div>
          {(t.sticky_notes || []).map((n, i) => (
            <div key={i} className="flex gap-1 items-center">
              <input value={n.title || ''} onChange={(e) => updateNote(i, 'title', e.target.value.slice(0, 40))} className="flex-1 px-1.5 py-0.5 rounded border border-gray-200 text-[11px]" />
              <select value={n.color || 'pink'} onChange={(e) => updateNote(i, 'color', e.target.value)} data-testid={`note-color-${i}`} className="px-1 py-0.5 rounded border border-gray-200 text-[11px]">
                {NOTE_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Live preview */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">Förhandsvisning</div>
        <div className="relative rounded-2xl overflow-hidden shadow-inner aspect-[3/4]" style={{
          background: pattern === 'custom' && t.custom_bg_b64
            ? `url(data:${t.custom_bg_mime || 'image/jpeg'};base64,${t.custom_bg_b64}) center/cover`
            : previewBg,
        }}>
          <div className="absolute inset-0 p-3 flex flex-col">
            <div className="text-center mb-2">
              <h2 className={`text-base tracking-[0.25em] font-bold ${
                t.title_font === 'script' ? 'italic' :
                t.title_font === 'sans' ? 'font-sans' :
                t.title_font === 'display' ? 'uppercase' : 'font-serif'
              }`} style={{ color: t.accent || '#9333EA' }}>
                {t.title || 'MIN PLAN'}
              </h2>
              <div className="text-[9px] italic" style={{ color: t.accent_soft }}>{t.subtitle}</div>
            </div>
            <div className="grid grid-cols-7 gap-0.5 flex-1">
              {Array.from({ length: 28 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-sm text-[8px] flex items-start p-0.5"
                  style={{ backgroundColor: t.cell_bg || 'white', border: `1px solid ${t.border_color}`, color: t.text_color }}>
                  {i % 4 === 0 ? '☆' : i + 1}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 pt-2">
          <button onClick={onCancel} disabled={busy} className="flex-1 px-2 py-1.5 rounded-xl bg-white border-2 border-gray-300 text-gray-700 text-xs font-bold">Avbryt</button>
          <button onClick={() => onSave(t)} disabled={busy} data-testid="theme-save" className="flex-1 px-2 py-1.5 rounded-xl bg-violet-500 hover:bg-violet-600 text-white text-xs font-bold flex items-center justify-center gap-1">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Spara
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ThemeStudio({ onClose, onThemeChanged }) {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [editing, setEditing] = useState(null);  // theme object being edited
  const [busy, setBusy] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/cal-theme/list`, { withCredentials: true });
      setItems(r.data?.items || []);
      const a = await axios.get(`${API}/cal-theme/active`, { withCredentials: true });
      setActiveId(a.data?.theme?.id || null);
    } catch (e) {/**/}
  };
  useEffect(() => { load(); }, []);

  const save = async (theme) => {
    setBusy(true);
    try {
      const payload = { id: theme.id, theme };
      const r = await axios.post(`${API}/cal-theme/save`, payload, { withCredentials: true });
      toast.success('Tema sparat 💜');
      setEditing(null);
      await load();
      // auto-activate if it's the first theme or was already active
      if (!activeId || activeId === r.data.id) {
        await axios.post(`${API}/cal-theme/activate`, { id: r.data.id }, { withCredentials: true });
        setActiveId(r.data.id);
        onThemeChanged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  const activate = async (id) => {
    try {
      await axios.post(`${API}/cal-theme/activate`, { id }, { withCredentials: true });
      setActiveId(id);
      toast.success('Tema aktiverat ✨');
      onThemeChanged?.();
    } catch {/**/}
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort temat?')) return;
    try {
      await axios.delete(`${API}/cal-theme/${id}`, { withCredentials: true });
      await load();
      if (activeId === id) { setActiveId(null); onThemeChanged?.(); }
    } catch {/**/}
  };

  const aiBuild = async () => {
    if (!aiDesc.trim()) return;
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/cal-theme/ai-generate`, { description: aiDesc }, { withCredentials: true, timeout: 60000 });
      setEditing({ ...r.data.theme, id: null });
      setAiDesc('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI kunde inte bygga tema');
    } finally { setAiBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-gradient-to-r from-violet-500 to-pink-500 px-5 py-3 flex justify-between items-center text-white">
          <h3 className="font-extrabold flex items-center gap-2"><PaletteIcon className="w-4 h-4" /> Tema-studio</h3>
          <button onClick={onClose} data-testid="theme-studio-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {editing ? (
            <ThemeEditor initial={editing} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
          ) : (
            <>
              {/* AI generate */}
              <div className="bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl p-3 space-y-2 border-2 border-violet-200">
                <div className="text-xs font-extrabold text-violet-800 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI bygger tema från din beskrivning</div>
                <div className="flex gap-1.5">
                  <input value={aiDesc} onChange={(e) => setAiDesc(e.target.value)} placeholder="t.ex. drömmig dimma och rosenträ" data-testid="ai-desc" className="flex-1 px-2 py-1.5 rounded border border-violet-200 text-sm" />
                  <button onClick={aiBuild} disabled={aiBusy || !aiDesc.trim()} data-testid="ai-build" className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1">
                    {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Bygg
                  </button>
                </div>
              </div>

              {/* Existing themes */}
              {items.length > 0 && (
                <div>
                  <div className="text-[11px] font-extrabold text-gray-500 uppercase tracking-widest mb-2">Mina teman</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {items.map((t) => (
                      <div key={t.id} className={`rounded-xl border-2 p-2 ${activeId === t.id ? 'border-violet-500 ring-2 ring-violet-200' : 'border-gray-200'}`} data-testid={`theme-item-${t.id}`}>
                        <div className="rounded-lg h-16 mb-1.5 border" style={{ background: `linear-gradient(${t.gradient_dir}, ${t.gradient_from}, ${t.gradient_via}, ${t.gradient_to})`, borderColor: t.border_color }} />
                        <div className="text-xs font-bold truncate" style={{ color: t.text_color }}>{t.name}</div>
                        <div className="flex gap-1 mt-1">
                          {activeId !== t.id && <button onClick={() => activate(t.id)} className="flex-1 text-[10px] py-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 font-bold">Använd</button>}
                          {activeId === t.id && <span className="flex-1 text-[10px] py-0.5 rounded bg-violet-500 text-white font-bold text-center"><Check className="w-3 h-3 inline" /> Aktiv</span>}
                          <button onClick={() => setEditing(t)} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 hover:bg-gray-50">Edit</button>
                          <button onClick={() => remove(t.id)} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 hover:bg-rose-50 text-rose-500"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Presets / new */}
              <div>
                <div className="text-[11px] font-extrabold text-gray-500 uppercase tracking-widest mb-2">Börja från en preset</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PRESETS.map((p, i) => (
                    <button key={i} onClick={() => setEditing({ ...p, id: null })} data-testid={`preset-${i}`} className="rounded-xl border-2 border-gray-200 hover:border-violet-400 p-2 text-left">
                      <div className="rounded-lg h-14 mb-1.5" style={{ background: `linear-gradient(${p.gradient_dir}, ${p.gradient_from}, ${p.gradient_via}, ${p.gradient_to})` }} />
                      <div className="text-xs font-bold">{p.name}</div>
                    </button>
                  ))}
                  <button onClick={() => setEditing({ ...PRESETS[0], id: null, name: 'Min nya' })} className="rounded-xl border-2 border-dashed border-gray-300 hover:border-violet-400 p-2 flex items-center justify-center text-xs font-bold text-gray-500" data-testid="theme-new">
                    <Plus className="w-4 h-4 mr-1" /> Skapa helt nytt
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
